import { isSignedOut } from "./api-error";

/**
 * Unattended-wallboard continuity rules (react-free so they are directly testable).
 *
 * The Production Monitor live board runs unattended on a QC screen. Two failure
 * modes must never take the board over:
 *
 *   1. the read source going away — the board keeps its last good snapshot and
 *      shows a quiet staleness stamp;
 *   2. the browser session expiring while nobody is at the screen — same
 *      treatment, plus a silent session refresh in the background.
 *
 * (2) is not hypothetical: better-auth's client session atom sets its data to
 * null on a 401 from /api/auth/get-session, and that refetch fires on window
 * focus / online / cross-tab broadcast. A screen wake on an idle board is
 * therefore enough to null the session, which used to send RequireAuth straight
 * to the sign-in landing and replace the whole board.
 *
 * While the session is being re-established nothing new is fetched — only the
 * snapshot already in memory stays on screen — so holding the view costs no
 * additional data reach.
 */

/** What the board is doing right now, from the wallboard reader's point of view. */
export type WallboardMode = "live" | "updating" | "stale" | "connecting" | "offline";

export interface WallboardConnection {
  mode: WallboardMode;
  /** User-facing status line. Plain plant English — no auth/system internals. */
  label: string;
  /**
   * True when the degradation looks like an expired session, so the caller
   * should attempt a silent session refresh rather than only re-polling data.
   */
  reauth: boolean;
}

export interface WallboardConnectionInput {
  /** A previous successful snapshot is in memory. */
  hasData: boolean;
  /** A refresh is in flight. */
  isFetching: boolean;
  /** The most recent refresh failed. */
  isError: boolean;
  /** HTTP status of that failure, when there was one. */
  errorStatus?: number;
  /** The client session has gone away underneath the board. */
  sessionExpired?: boolean;
  /** Preformatted local time of the snapshot on screen, e.g. "14:32". */
  updatedAtLabel: string;
}

export function wallboardConnection({
  hasData,
  isFetching,
  isError,
  errorStatus,
  sessionExpired = false,
  updatedAtLabel,
}: WallboardConnectionInput): WallboardConnection {
  const degraded = isError || sessionExpired;
  const reauth = sessionExpired || errorStatus === 401;

  if (!hasData) {
    return degraded
      ? { mode: "offline", label: "Live production is temporarily unavailable", reauth }
      : { mode: "connecting", label: "Connecting to live production…", reauth: false };
  }

  // Degradation outranks "updating": while a failed refresh is being retried the
  // board must keep saying the figures are the last known good ones.
  if (degraded) {
    return {
      mode: "stale",
      label: `Last updated ${updatedAtLabel} · reconnecting`,
      reauth,
    };
  }

  if (isFetching) return { mode: "updating", label: "Updating…", reauth: false };

  return { mode: "live", label: `Updated ${updatedAtLabel}`, reauth: false };
}

/**
 * Should an already-rendered authenticated view stay on screen after the client
 * session disappears?
 *
 * Only for a registered unattended surface that HAD a session in this page
 * lifetime — i.e. the board was signed in and the session lapsed underneath it.
 * A cold load with no session is still a redirect to sign in, so this never
 * becomes a way to reach the app unauthenticated.
 */
export function shouldHoldSignedOutView({
  hasSession,
  unattended,
  hadSession,
}: {
  hasSession: boolean;
  unattended: boolean;
  hadSession: boolean;
}): boolean {
  return !hasSession && unattended && hadSession;
}

/**
 * #4836: Display Mode's staleness stamp, the other unattended screen. Its
 * figures poll every `interval` (TanStack restarts the interval as each
 * reply settles), so the first poll after a good reply is due one interval
 * later and the next about two intervals later. Once the oldest figures on
 * screen are two and a half intervals old, the first of those polls has been
 * failing, or getting no answer, for longer than one interval. Halfway
 * between polls none is due, so a reply about to land never flashes the
 * stamp up for a moment; a fetch still open by then is itself a stale screen.
 */
export const STALE_AFTER_INTERVALS = 2.5;

export interface FiguresStaleness {
  /** When the oldest figures on screen were fetched. */
  since: number;
  /** When the stamp shows, if no newer reply has landed by then. */
  showsAt: number;
}

/**
 * From when each figures query last answered (0 when it never has), the
 * oldest reply on screen and when its stamp shows. Null before any query has
 * answered, so the first load never shows a stamp.
 */
export function figuresStaleness(repliedAt: readonly number[], interval: number): FiguresStaleness | null {
  const answered = repliedAt.filter((at) => at > 0);
  if (answered.length === 0) return null;
  const since = Math.min(...answered);
  return { since, showsAt: since + STALE_AFTER_INTERVALS * interval };
}

/** One query's latest outcome, as TanStack reports it. */
export interface PollOutcome {
  /** When it last answered (0 when it never has). */
  dataUpdatedAt: number;
  /** When it last failed. */
  errorUpdatedAt: number;
  /** Its latest attempt failed. */
  isError: boolean;
  error: unknown;
}

/** #4836: the newest 401 the polls hold right now, 0 when none does. */
export function newestSignedOutAt(outcomes: readonly PollOutcome[]): number {
  let at = 0;
  for (const outcome of outcomes) {
    if (outcome.isError && isSignedOut(outcome.error)) at = Math.max(at, outcome.errorUpdatedAt);
  }
  return at;
}

/**
 * #4836: do the screen's own polls say its session has lapsed? Yes when the
 * newest 401 seen is no older than the newest answer (a tie holds: a reload
 * is the costlier mistake). Every one of these routes needs the session, so
 * only an answer after the 401 shows the session is back. A later 503, or a
 * refetch that clears a never-answered query's error, proves nothing, so the
 * caller passes the newest 401 it has seen, not only the ones still held.
 */
export function pollsSignedOut(outcomes: readonly PollOutcome[], signedOutAt = newestSignedOutAt(outcomes)): boolean {
  let answeredAt = 0;
  for (const outcome of outcomes) answeredAt = Math.max(answeredAt, outcome.dataUpdatedAt);
  return signedOutAt > 0 && signedOutAt >= answeredAt;
}
